import React, { useState, useEffect, useRef } from 'react';

let leafletLoadingPromise = null;

export function loadLeaflet() {
  if (window.L) return Promise.resolve();
  if (leafletLoadingPromise) return leafletLoadingPromise;

  leafletLoadingPromise = new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return leafletLoadingPromise;
}

export default function MapPickerModal({ initialLat, initialLng, onConfirm, onClose }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [address, setAddress] = useState('');
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then(() => {
      if (cancelled) return;
      const L = window.L;
      const startLat = initialLat || 30.3753;
      const startLng = initialLng || 69.3451; // roughly centered on Pakistan by default

      const map = L.map(mapRef.current).setView([startLat, startLng], initialLat ? 14 : 5);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      const marker = L.marker([startLat, startLng], { draggable: true }).addTo(map);
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        handlePinMove(pos.lat, pos.lng);
      });
      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        handlePinMove(e.latlng.lat, e.latlng.lng);
      });

      mapInstanceRef.current = map;
      markerRef.current = marker;
      setReady(true);

      if (initialLat && initialLng) {
        handlePinMove(initialLat, initialLng);
      }
    }).catch(() => setReady(false));

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePinMove = async (lat, lng) => {
    setCoords({ lat, lng });
    setAddress('Looking up address...');
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await res.json();
      setAddress(data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    } catch (e) {
      setAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    }
  };

  const searchLocation = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`);
      const data = await res.json();
      setSearchResults(data);
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    if (mapInstanceRef.current && markerRef.current) {
      mapInstanceRef.current.setView([lat, lng], 15);
      markerRef.current.setLatLng([lat, lng]);
    }
    setAddress(result.display_name);
    setCoords({ lat, lng });
    setSearchResults([]);
    setSearchQuery(result.display_name);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Your browser does not support location detection.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (mapInstanceRef.current && markerRef.current) {
          mapInstanceRef.current.setView([latitude, longitude], 15);
          markerRef.current.setLatLng([latitude, longitude]);
        }
        handlePinMove(latitude, longitude);
        setLocating(false);
      },
      () => {
        alert('Could not get your location - please allow location access, or tap the map to pick manually.');
        setLocating(false);
      }
    );
  };

  const confirm = () => {
    if (!coords) {
      alert('Tap the map (or use current location) to drop a pin first.');
      return;
    }
    onConfirm({ address, latitude: coords.lat, longitude: coords.lng });
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 520, width: '100%', padding: 20, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>📍 Pick your location</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <button onClick={useCurrentLocation} disabled={locating} style={{ marginBottom: 10, padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 8, background: '#E1F5EE', color: '#0F6E56', cursor: 'pointer', alignSelf: 'flex-start' }}>
          {locating ? 'Locating...' : '📍 Use my current location'}
        </button>

        <div style={{ marginBottom: 10, position: 'relative' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              placeholder="🔍 Search for a place or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchLocation(); } }}
              style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' }}
            />
            <button onClick={searchLocation} disabled={searching} style={{ padding: '8px 14px', fontSize: 13, border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>
              {searching ? '...' : 'Search'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, marginTop: 4, zIndex: 10, maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              {searchResults.map((r, i) => (
                <div
                  key={i}
                  onClick={() => selectSearchResult(r)}
                  style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', borderBottom: i < searchResults.length - 1 ? '1px solid #eee' : 'none' }}
                >
                  📍 {r.display_name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div ref={mapRef} style={{ width: '100%', height: 300, borderRadius: 10, background: '#eee', marginBottom: 10 }} />
        {!ready && <p style={{ fontSize: 12, color: '#999', textAlign: 'center' }}>Loading map...</p>}

        <p style={{ fontSize: 13, color: '#333', marginBottom: 10, minHeight: 20 }}>
          {address || 'Tap the map, drag the pin, or use current location.'}
        </p>

        <button onClick={confirm} style={{ padding: '12px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>
          Confirm this location
        </button>
      </div>
    </div>
  );
}
